<template>
  <teleport to="body">
    <div class="mer-overlay" @click.self="handleClose">
      <div class="mer-dialog">
        <!-- Botón cerrar -->
        <button
          type="button"
          class="mer-close"
          @click="handleClose"
        >
          ✕
        </button>

        <!-- Header -->
        <header class="mer-header">
          <h3 class="mer-title">
            Editar complementos
          </h3>
          <p class="mer-subtitle">
            {{ unidad?.nombre_producto || 'Producto' }} — {{ unidad?.numero_serie || 'Sin serie' }}
          </p>

          <div class="mer-meta" v-if="unidad">
            <span class="mer-meta-chip">
              ID unidad: {{ unidad.id_unidad }}
            </span>
            <span
              v-if="unidad.estado"
              class="mer-meta-chip"
            >
              Estado: {{ unidad.estado }}
            </span>
            <span
              v-if="unidad.categoria_nombre"
              class="mer-meta-chip mer-meta-chip--soft"
            >
              {{ unidad.categoria_nombre }}
            </span>
          </div>
        </header>

        <!-- Buscador + contador -->
        <section class="mer-search-section">
          <div class="mer-search-row">
            <div class="mer-search-wrapper">
              <input
                v-model="busqueda"
                type="text"
                class="mer-search-input"
                placeholder="Buscar por nombre o número de serie..."
              />
            </div>
            <div class="mer-counter">
              <span class="mer-counter-pill">
                {{ complementosFiltrados.length }} resultados
              </span>
            </div>
          </div>

          <!-- Resumen seleccionados -->
          <div
            v-if="seleccionados.length"
            class="mer-selected-row"
          >
            <span class="mer-selected-label">
              Seleccionados ({{ seleccionados.length }}):
            </span>
            <div class="mer-selected-chips">
              <button
                v-for="sel in seleccionadosDetalle"
                :key="sel.id_unidad"
                type="button"
                class="mer-selected-chip"
                @click="toggleSeleccion(sel)"
              >
                <span class="mer-selected-chip-text">
                  {{ sel.nombre_producto || 'Complemento' }} — {{ sel.numero_serie }}
                </span>
                <span class="mer-selected-chip-x">✕</span>
              </button>
            </div>
          </div>
        </section>

        <!-- Lista de complementos -->
        <section class="mer-list-section">
          <div class="mer-list" v-if="complementosFiltrados.length">
            <button
              v-for="c in complementosFiltrados"
              :key="c.id_unidad"
              type="button"
              class="mer-list-item"
              :class="{ 'is-selected': estaSeleccionado(c.id_unidad) }"
              @click="toggleSeleccion(c)"
            >
              <div class="mer-list-item-main">
                <p class="mer-list-item-name">
                  {{ c.nombre_producto || 'Complemento' }}
                </p>
                <p class="mer-list-item-serial">
                  Serie: {{ c.numero_serie }}
                </p>
              </div>
              <div class="mer-list-item-right">
                <span
                  class="mer-badge"
                  :class="estaSeleccionado(c.id_unidad) ? 'mer-badge--on' : 'mer-badge--off'"
                >
                  {{ estaSeleccionado(c.id_unidad) ? 'Seleccionado' : 'Disponible' }}
                </span>
              </div>
            </button>
          </div>

          <p
            v-else
            class="mer-empty"
          >
            No se encontraron complementos con ese filtro.
          </p>
        </section>

        <!-- Footer -->
        <footer class="mer-footer">
          <button
            type="button"
            class="mer-btn mer-btn--ghost"
            @click="handleClose"
            :disabled="saving"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="mer-btn mer-btn--primary"
            @click="guardarCambios"
            :disabled="saving"
          >
            {{ saving ? 'Guardando…' : 'Guardar cambios' }}
          </button>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ModalEditarRelacion',
  props: {
    unidad: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      complementosDisponibles: [],
      seleccionados: [],
      busqueda: '',
      saving: false,
    };
  },
  computed: {
    complementosFiltrados() {
      const list = Array.isArray(this.complementosDisponibles)
        ? this.complementosDisponibles
        : [];
      const term = this.busqueda.trim().toLowerCase();

      if (!term) return list;

      return list.filter((c) => {
        const texto =
          `${c.nombre_producto || ''} ${c.numero_serie || ''}`.toLowerCase();
        return texto.includes(term);
      });
    },
    // Para mostrar chips de seleccionados con info completa
    seleccionadosDetalle() {
      if (!this.seleccionados.length) return [];
      const map = new Map(
        this.complementosDisponibles.map((c) => [c.id_unidad, c])
      );
      return this.seleccionados
        .map((id) => map.get(id))
        .filter((x) => !!x);
    },
  },
  mounted() {
    // Selección inicial: los complementos ya relacionados
    if (this.unidad && Array.isArray(this.unidad.complementos)) {
      this.seleccionados = this.unidad.complementos.map((c) => c.id_unidad);
    } else {
      this.seleccionados = [];
    }
    this.obtenerComplementos();
  },
  methods: {
    async obtenerComplementos() {
      if (!this.unidad) return;

      try {
        const res = await axios.get('/api/almacen/unidades/listar');
        const raw = Array.isArray(res.data) ? res.data : [];

        // Posibles complementos: otras unidades (no la principal)
        const filtrados = raw.filter(
          (u) => u.id_unidad !== this.unidad.id_unidad
        );

        // Dedupe por id_unidad
        const seen = new Set();
        this.complementosDisponibles = filtrados.filter((u) => {
          if (seen.has(u.id_unidad)) return false;
          seen.add(u.id_unidad);
          return true;
        });
      } catch (e) {
        console.error('Error al obtener complementos', e);
      }
    },
    toggleSeleccion(unidad) {
      const id = unidad.id_unidad ?? unidad;
      const idx = this.seleccionados.indexOf(id);
      if (idx >= 0) {
        this.seleccionados.splice(idx, 1);
      } else {
        this.seleccionados.push(id);
      }
    },
    estaSeleccionado(id) {
      return this.seleccionados.includes(id);
    },
    async guardarCambios() {
      if (!this.unidad) return;
      this.saving = true;
      try {
        const body = {
          id_unidad_principal: this.unidad.id_unidad,
          nuevos_complementos: this.seleccionados,
        };
        await axios.post('/api/almacen/relacion-unidades/actualizar', body);
        this.$emit('guardado');
        this.handleClose();
      } catch (err) {
        console.error('Error al guardar relación actualizada', err);
      } finally {
        this.saving = false;
      }
    },
    handleClose() {
      if (this.saving) return;
      this.$emit('cerrar');
    },
  },
};
</script>

<style scoped>
.mer-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(15, 23, 42, 0.55),
      rgba(15, 23, 42, 0.94)
    );
  backdrop-filter: blur(18px);
}

.mer-dialog {
  position: relative;
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  border-radius: 1.6rem;
  padding: 1.5rem 1.4rem 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.32),
      rgba(15, 23, 42, 0.97)
    );
  border: 1px solid rgba(148, 163, 184, 0.7);
  box-shadow:
    0 20px 50px rgba(15, 23, 42, 0.98),
    0 0 0 1px rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Scroll interno elegante */
.mer-dialog::-webkit-scrollbar {
  width: 6px;
}
.mer-dialog::-webkit-scrollbar-track {
  background: transparent;
}
.mer-dialog::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.6);
  border-radius: 999px;
}

.mer-close {
  position: absolute;
  top: 0.6rem;
  right: 0.7rem;
  border: none;
  background: transparent;
  color: #cbd5f5;
  cursor: pointer;
  font-size: 0.9rem;
}

/* Header */
.mer-header {
  margin-bottom: 0.9rem;
}

.mer-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.mer-subtitle {
  font-size: 0.82rem;
  color: #9ca3af;
}

.mer-meta {
  margin-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.mer-meta-chip {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  padding: 0.18rem 0.55rem;
  font-size: 0.7rem;
  color: #e5e7eb;
  background: rgba(15, 23, 42, 0.9);
}

.mer-meta-chip--soft {
  border-style: dashed;
  color: #cbd5f5;
}

/* Search section */
.mer-search-section {
  margin-bottom: 0.75rem;
}

.mer-search-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.5rem;
}

.mer-search-wrapper {
  flex: 1 1 0;
  min-width: 0;
}

.mer-search-input {
  width: 100%;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.9);
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  color: #e5e7eb;
  outline: none;
}

.mer-search-input::placeholder {
  color: #64748b;
}

.mer-counter {
  flex-shrink: 0;
}

.mer-counter-pill {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.75);
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
  color: #cbd5f5;
}

/* Selected row */
.mer-selected-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.mer-selected-label {
  font-size: 0.75rem;
  color: #9ca3af;
}

.mer-selected-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  max-height: 72px;
  overflow-y: auto;
}

.mer-selected-chips::-webkit-scrollbar {
  height: 4px;
}
.mer-selected-chips::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.6);
  border-radius: 999px;
}

.mer-selected-chip {
  border-radius: 999px;
  border: 1px solid rgba(59, 130, 246, 0.9);
  background: rgba(37, 99, 235, 0.18);
  color: #e5e7eb;
  font-size: 0.72rem;
  padding: 0.2rem 0.45rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
}

.mer-selected-chip-text {
  white-space: nowrap;
}

.mer-selected-chip-x {
  font-size: 0.7rem;
}

/* List */
.mer-list-section {
  flex: 1 1 auto;
  min-height: 0;
  margin-bottom: 0.85rem;
}

.mer-list {
  border-radius: 1rem;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: rgba(15, 23, 42, 0.92);
  padding: 0.45rem 0.35rem;
  max-height: 260px;
  overflow-y: auto;
}

.mer-list::-webkit-scrollbar {
  width: 6px;
}
.mer-list::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.6);
  border-radius: 999px;
}

.mer-list-item {
  width: 100%;
  border-radius: 0.9rem;
  border: 1px solid transparent;
  padding: 0.45rem 0.55rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.12s ease,
    border-color 0.12s ease,
    transform 0.08s ease;
}

.mer-list-item + .mer-list-item {
  margin-top: 0.25rem;
}

.mer-list-item-main {
  flex: 1 1 0;
  min-width: 0;
}

.mer-list-item-name {
  font-size: 0.82rem;
  color: #e5e7eb;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.mer-list-item-serial {
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.mer-list-item-right {
  flex-shrink: 0;
}

.mer-list-item:hover {
  background: rgba(15, 23, 42, 0.96);
  border-color: rgba(148, 163, 184, 0.9);
}

.mer-list-item.is-selected {
  background: rgba(37, 99, 235, 0.16);
  border-color: rgba(37, 99, 235, 0.9);
}

.mer-badge {
  border-radius: 999px;
  font-size: 0.68rem;
  padding: 0.18rem 0.45rem;
  border: 1px solid transparent;
}

.mer-badge--on {
  background: rgba(34, 197, 94, 0.16);
  border-color: rgba(34, 197, 94, 0.9);
  color: #bbf7d0;
}

.mer-badge--off {
  background: rgba(15, 23, 42, 0.8);
  border-color: rgba(148, 163, 184, 0.7);
  color: #cbd5f5;
}

.mer-empty {
  font-size: 0.8rem;
  color: #9ca3af;
}

/* Footer */
.mer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
}

.mer-btn {
  border-radius: 999px;
  font-size: 0.8rem;
  padding: 0.4rem 0.9rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    opacity 0.12s ease;
}

.mer-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.mer-btn--ghost {
  background: rgba(15, 23, 42, 0.4);
  border-color: rgba(148, 163, 184, 0.6);
  color: #e5e7eb;
}

.mer-btn--ghost:hover:not(:disabled) {
  background: rgba(15, 23, 42, 0.7);
}

.mer-btn--primary {
  background: rgba(37, 99, 235, 0.96);
  border-color: rgba(59, 130, 246, 1);
  color: #eff6ff;
}

.mer-btn--primary:hover:not(:disabled) {
  background: rgba(37, 99, 235, 1);
}

/* Responsive */
@media (max-width: 640px) {
  .mer-dialog {
    padding: 1.2rem 1rem 1rem;
    border-radius: 1.3rem;
  }
  .mer-search-row {
    flex-direction: column;
    align-items: stretch;
  }
  .mer-counter {
    align-self: flex-start;
  }
}
</style>
